'use client';

import { Collection } from 'react-aria-components/Collection';
import type { Selection } from 'react-aria-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Description, Modal } from '@heroui/react';
import { FileTree, useFileTree } from '@heroui-pro/react';
import { buildTree } from '@hs/shared';
import { trpc } from '@/lib/trpc';
import type { TreeSelectNode } from '@/components/custom';
import type { KnowledgeBasesScope } from '../../components/KnowledgeBaseHeader';
import { KNOWLEDGE_BASE_CATEGORY_TYPE } from '../../components/constants';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type CategoryItem = { id: string; name?: string | null; parentId?: string | null; order?: number };

interface CategoryTreeNode extends TreeSelectNode {
  category: CategoryItem;
}

function renderCategoryTreeItem(item: CategoryTreeNode) {
  const hasKids = !!(item.children && item.children.length > 0);
  return (
    <FileTree.Item className="group" dragIcon={false} id={item.id} textValue={item.label} title={item.label}>
      {hasKids ? (
        <Collection items={item.children as CategoryTreeNode[]}>
          {(child) => renderCategoryTreeItem(child as CategoryTreeNode)}
        </Collection>
      ) : null}
    </FileTree.Item>
  );
}

export interface CategoryPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: KnowledgeBasesScope;
  isSubmitting?: boolean;
  onConfirm: (categoryIds: string[]) => void | Promise<void>;
}

export function CategoryPickerModal({
  open,
  onOpenChange,
  scope,
  isSubmitting = false,
  onConfirm,
}: CategoryPickerModalProps) {
  const categoryScope = scope === 'my' ? 'MINE' : 'SPACE';
  const categoryTreeInput = useMemo(
    () => ({ type: KNOWLEDGE_BASE_CATEGORY_TYPE, scope: categoryScope }),
    [categoryScope],
  );

  const { data: categoryFlatList = [], isLoading } = trpc.categories.getCategoryTree.useQuery(
    categoryTreeInput as never,
    { enabled: open, staleTime: 0 },
  );

  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedKeys(new Set());
    }
  }, [open]);

  const categoryFileItems: CategoryTreeNode[] = useMemo(() => {
    const flat = (categoryFlatList as CategoryItem[]).filter((c) => c.id != null);
    if (flat.length === 0) return [];
    const tree = buildTree(flat, {
      parentField: 'parentId',
      childrenField: 'children',
      sortBy: 'order',
      sortOrder: 'asc',
    }) as { id: string; name?: string | null; parentId?: string | null; children?: unknown[] }[];
    const convert = (nodes: typeof tree): CategoryTreeNode[] =>
      nodes.map((node) => ({
        id: node.id,
        label: node.name ?? '',
        category: { id: node.id, name: node.name, parentId: node.parentId },
        children: node.children?.length ? convert(node.children as typeof tree) : undefined,
      }));
    return convert(tree);
  }, [categoryFlatList]);

  const { expandableKeys } = useFileTree({ items: categoryFileItems });

  const handleModalOpenChange = useCallback(
    (next: boolean) => {
      if (!next && !isSubmitting) {
        onOpenChange(false);
      }
    },
    [isSubmitting, onOpenChange],
  );

  const selectedCategoryIds = useMemo(() => {
    if (selectedKeys === 'all') {
      const collect = (nodes: CategoryTreeNode[]): string[] =>
        nodes.flatMap((n) => [n.id, ...(n.children?.length ? collect(n.children as CategoryTreeNode[]) : [])]);
      return collect(categoryFileItems);
    }
    if (selectedKeys instanceof Set && selectedKeys.size > 0) {
      return [...selectedKeys].map(String);
    }
    return [];
  }, [selectedKeys, categoryFileItems]);

  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm(selectedCategoryIds);
    } catch {
      /* mutation onError / parent handles feedback */
    }
  }, [onConfirm, selectedCategoryIds]);

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop isDismissable={!isSubmitting}>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="flex max-h-[85vh] w-full max-w-lg min-w-0 flex-col">
            <Modal.Header className="shrink-0 gap-1 border-b border-[var(--border)] px-6 py-4">
              <Modal.Heading className="text-lg font-semibold">选择分类</Modal.Heading>
              <Description className="text-muted-foreground text-sm">
                勾选要将所选知识库加入的一个或多个分类（可多选）
              </Description>
            </Modal.Header>
            <Modal.Body className="flex min-h-0 flex-1 flex-col gap-3 p-4">
              {isLoading ? (
                <div className="text-muted-foreground py-6 text-center text-sm">分类加载中…</div>
              ) : categoryFileItems.length === 0 ? (
                <div className="text-muted-foreground py-6 text-center text-sm">暂无自定义分类，请先在侧栏新建分类</div>
              ) : (
                <div className="min-h-[200px] max-h-[50vh] min-w-0 overflow-y-auto rounded-md border border-[var(--border)] p-2">
                  <FileTree
                    aria-label="选择分类"
                    className="[&_[data-slot=file-tree-icon]]:hidden [&_[data-slot=file-tree-item]]:mb-1"
                    defaultExpandedKeys={expandableKeys}
                    items={categoryFileItems}
                    selectedKeys={selectedKeys}
                    selectionBehavior="toggle"
                    selectionMode="multiple"
                    showGuideLines="hover"
                    size="md"
                    onSelectionChange={setSelectedKeys}
                  >
                    {(item) => renderCategoryTreeItem(item as CategoryTreeNode)}
                  </FileTree>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="shrink-0 border-t border-[var(--border)]">
              <Button variant="secondary" isDisabled={isSubmitting} onPress={() => onOpenChange(false)}>
                取消
              </Button>
              <Button
                variant="primary"
                isDisabled={isSubmitting || categoryFileItems.length === 0 || selectedCategoryIds.length === 0}
                isPending={isSubmitting}
                onPress={() => void handleConfirm()}
              >
                确定
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
